import React, { useState, useEffect } from "react";
import { Select, Tooltip } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { CustomButton, CustomTitle } from "../../../components";
import ShipmentPreview from "./ShipmentPreview";
import ShipmentPreviewCard from "./ShipmentPreviewCard";
import "./StepOneMapping.css";

const { Option } = Select;

const ZoomLogiFieldMap = {
  reference_number: "Reference number",
  courier: "Transport provider",
  courier_tracking_id: "Transport provider Tracking ID",
  sensor: "Sensor provider",
  sensor_tracking_id: "Sensor ID",
  products: "Products",
  teams: "Teams",
  from_location: "Origin",
  to_location: "Destination",
  link_code: "Link Code",
  ignore_sensor_measurement_at_start: "Ignore sensor measurement at start",
};

const requiredFields = ["courier_tracking_id", "reference_number"];

const dummyCard = {
  id: "SH123456",
  reference_number: "REF-987654",
  status: "IN TRANSIT",
  type: 1,
  from_location: {
    name: "Colorado Warehouse",
    parsed_address: { PlaceName: "Denver", StateName: "CO", ZipCode: "80202" },
  },
  to_location: {
    name: "General Hospital",
    parsed_address: { PlaceName: "Austin", StateName: "TX", ZipCode: "73301" },
  },
  scheduled_pickup_date: "2024-08-10",
  scheduled_delivery_date: "2024-08-13",
  courier: { title: "cold chain express" },
  teams: [
    { name: "Team Alpha", color: "#266EF1" },
    { name: "Team Beta", color: "#FF9800" },
  ],
  legs_info: {
    legs: [
      { status: "travelled", mode: "truck", distribution: 50 },
      { status: "pending", mode: "sky", distribution: 50 },
    ],
  },
};

const StepOneMapping = ({
  onCancel,
  onNext,
  fieldMappings,
  setFieldMappings,
  enabled,
  mapping,
  handleToggle,
  handleMappingChange,
  setMapping,
}) => {
  const [previewVisible, setPreviewVisible] = useState(true);

  useEffect(() => {
    const initialMapping = {};
    fieldMappings?.forEach((f, index) => {
      if (f.enable) {
        initialMapping[index] = f.actual_name;
      }
    });
    setMapping(initialMapping);
  }, [fieldMappings, setMapping]);

  const previewData = {
    reference_number: "",
    courier: null,
    sensor: null,
    courier_tracking_id: "",
    sensor_tracking_id: "",
  };

  fieldMappings?.forEach((field, index) => {
    const mappedTo = mapping[index];
    const isEnabled = field.enable !== false;

    if (isEnabled && ZoomLogiFieldMap[mappedTo]) {
      switch (mappedTo) {
        case "reference_number":
        case "courier_tracking_id":
        case "sensor_tracking_id":
          previewData[mappedTo] = field.header_name;
          break;
        case "courier":
        case "sensor":
          previewData[mappedTo] = { title: field.header_name };
          break;
        default:
          break;
      }
    }
  });

  const dynamicCard = {
    ...dummyCard,
    ...previewData,
  };

  const valueMap = {};
  fieldMappings?.forEach((item, index) => {
    if (item.actual_name && item.enable) {
      valueMap[item.actual_name + "_" + index] = item.value;
    }
  });

  const getValue = (key) => {
    const match = fieldMappings.find(
      (f) => f.actual_name === key && f.enable !== false
    );
    return match ? match.value : "--";
  };

  const shipment = {
    reference_number: getValue("reference_number"),
    courier: { title: getValue("courier") },
    sensor: {
      type: getValue("sensor"),
      id: getValue("sensor_tracking_id"),
    },
    courier_tracking_id: getValue("courier_tracking_id"),
    from_location: {
      name: getValue("from_location"),
      parsed_address: { PlaceName: getValue("from_location") },
    },
    to_location: {
      name: getValue("to_location"),
      parsed_address: { PlaceName: getValue("to_location") },
    },
    product: {
      name: getValue("products"),
      product_type: "--",
    },
    contact_phone: "--",
    scheduled_pickup_date: getValue("scheduled_pickup_date"),
    scheduled_delivery_date: getValue("scheduled_delivery_date"),
    estimated_delivery_date: "--",
    delivery_status: "--",
    monitor_info: {
      temperature: "--",
      humidity: "--",
      battery: "--",
    },
    teams: [{ name: "Team Alpha", color: "#266EF1" }],
    volume: getValue("volume"),
    weight_kg: getValue("weight_kg"),
    transport_provider_eta: "--",
  };

  const areRequiredFieldsEnabled = requiredFields.every((field) =>
    fieldMappings.some((f) => f.actual_name === field && f.enable !== false)
  );

  const hasAtLeastOneProvider = fieldMappings.some(
    (f) =>
      (f.actual_name === "courier" || f.actual_name === "sensor") &&
      f.enable !== false
  );

  const isFormValid = areRequiredFieldsEnabled && hasAtLeastOneProvider;

  return (
    <>
      <CustomTitle
        text="Bulk upload"
        fontSize="20px"
        lineHeight="28px"
        fontWeight="700"
        marginBottom="4px"
      />
      <p
        style={{
          fontSize: "14px",
          color: "#5E5E5E",
          marginBottom: "15px",
          marginTop: "20px",
        }}
      >
        Review and match each column to the correct data field to ensure
        accurate importing
      </p>

      <div className="step-one-mapping">
        <div className={`step-one-left ${!previewVisible ? "full-width" : ""}`}>
          <div className="step-one-header">
            <div style={{ width: "37%", marginLeft: "7%" }}>
              <p style={{ fontWeight: 600, fontSize: 12, color: "#5E5E5E" }}>
                From table.xlsx
              </p>
            </div>
            <div style={{ width: "10%", textAlign: "center" }}></div>
            <div style={{ width: "45%" }}>
              <p style={{ fontWeight: 600, fontSize: 12, color: "#5E5E5E" }}>
                ZoomLogi data field (<span style={{ color: "red" }}>*</span> =
                required)
              </p>
            </div>
          </div>

          {fieldMappings?.map((field, index) => (
            <div className="step-one-row" key={index}>
              <div className="step-one-col">
                <div className="cl-toggle-switch">
                  <label className="cl-switch">
                    <input
                      style={{ height: 36 }}
                      type="checkbox"
                      checked={field.enable ?? true}
                      onChange={(e) => handleToggle(index, e.target.checked)}
                    />
                    <span className="cl-slider"></span>
                  </label>
                </div>
                <input
                  disabled={!field.enable}
                  value={field.header_name}
                  readOnly
                  style={{
                    flex: 1,
                    height: 36,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: !field.enable
                      ? "none"
                      : "1px solid #ccc",
                    backgroundColor: !field.enable
                      ? "#f5f5f5"
                      : "#fff",
                  }}
                />
              </div>

              {field.enable !== false && (
                <>
                  <div className="step-one-arrow">
                    <ArrowRightOutlined style={{ fontSize: 16 }} />
                  </div>
                  <div className="step-one-select">
                    <Select
                      style={{
                        height: 36,
                        width: "100%",
                        backgroundColor: "#F3F3F3",
                        borderRadius: 6,
                      }}
                      placeholder="Select field"
                      value={mapping[index] || undefined}
                      onChange={(val) => handleMappingChange(index, val)}
                    >
                      {Object.entries(ZoomLogiFieldMap).map(
                        ([value, label]) => (
                          <Option key={value} value={value}>
                            {label}
                            {requiredFields.includes(value) ? " *" : ""}
                          </Option>
                        )
                      )}
                    </Select>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {previewVisible && (
          <div className="shipment-preview">
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Shipment preview
            </div>
            <div style={{ fontSize: 13, marginBottom: 16 }}>
              <ShipmentPreviewCard
                shipment={shipment}
                card={dynamicCard}
                toggleDrawer={(action, type, id) =>
                  console.log(action, type, id)
                }
                drawerState={{ isOpen: false, shipmentId: "SH123456" }}
              />
            </div>
            <div
              style={{
                maxHeight: "520px",
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              className="no-scrollbar"
            >
              <ShipmentPreview shipment={shipment} />
            </div>
          </div>
        )}

        <button
          onClick={() => setPreviewVisible((prev) => !prev)}
          className={`toggle-preview-btn ${
            previewVisible ? "arrow-right" : "arrow-left"
          }`}
        >
          {previewVisible ? (
            <Tooltip title="Collapse preview">
              <ArrowRightOutlined />
            </Tooltip>
          ) : (
            <ArrowLeftOutlined />
          )}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 24,
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <CustomButton
          key="cancel"
          width={"80px"}
          onClick={onCancel}
          text={"Cancel"}
          bgColor="transparent"
          color="black"
          fontSize="14px"
          fontWeight="600"
          boxShadow="none"
          height="36px"
        />
        <CustomButton
          key="next"
          text="Next"
          width={"80px"}
          height={36}
          borderRadius={8}
          bgColor="#000"
          color="#fff"
          border="0px"
          fontSize={14}
          fontWeight={500}
          isFormValid={!isFormValid}
          onClick={onNext}
        />
      </div>
    </>
  );
};

export default StepOneMapping;
